import cx from 'classnames';
import {indexOf, map, orderBy} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionHistoryApi} from 'api';
import {Icon, Loader, ParticipantName, PredefinedNotifierType, Tooltip} from 'components';
import {InformationModal} from 'components/modals';
import {useApiData} from 'hooks';
import {
    AnyParticipant, AsyncRequestStatusName, AsyncRequestTypeName, ParticipantAsyncRequest,
    SumaDocumentVerificationSDKData
} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {StatusIcon} from 'pages/User/Conference/StatusIcon';
import {apiUtils, buildTranslationKey} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.PARTICIPANT_ASYNC_REQUESTS_MODAL;
const T_KEYS_SESSION = TRANSLATION_KEYS.COMMON.SESSION;

const TYPES_ORDER = [
    AsyncRequestTypeName.UploadSelfieVerification,
    AsyncRequestTypeName.Geolocation,
    AsyncRequestTypeName.IdAnalyzerFaceVerification,
    AsyncRequestTypeName.SumaDocumentVerification,
    AsyncRequestTypeName.SumaIPVerification,
    AsyncRequestTypeName.SumaEmailVerification,
    AsyncRequestTypeName.SumaPhoneVerification,
];

const DEFAULT_FILENAME = 'report';

type Props = {
    open: boolean,
    sessionId: string;
    participant: AnyParticipant;
    asyncRequests?: Partial<ParticipantAsyncRequest>[];
    onClose: () => void;
}

export const ParticipantAsyncRequestsHistoryModal: React.FC<Props> = ({
    open, sessionId, asyncRequests, participant, onClose
}) => {
    const {t} = useTranslation();

    const downloadFile = useCallback((pdfData: ArrayBuffer, ...args: any) => {
        const filename = args?.[0]?.[0]?.data?.verificationId || DEFAULT_FILENAME;
        apiUtils.saveBlob(`${filename}.pdf`, [pdfData], {type: 'application/pdf'});
    }, []);

    const getVerificationId = useCallback((asyncRequest: Partial<ParticipantAsyncRequest>) =>
        (asyncRequest?.data as SumaDocumentVerificationSDKData)?.verificationId, []);

    const buildNotifierErrorProps = useCallback(() => ({
        title: t(T_KEYS.REPORT_ERROR)
    }), [t]);

    const getSumaReportFn = useCallback((asyncRequest: Partial<ParticipantAsyncRequest>) => {
        const verificationId = getVerificationId(asyncRequest);
        return sessionHistoryApi.getSumaReport(sessionId, participant.id, verificationId);
    }, [sessionId, participant, getVerificationId]);

    const {
        send: getSumaReport,
        loading: sumaReportLoading
    } = useApiData(getSumaReportFn, {
        onSuccess: downloadFile,
        notificationType: PredefinedNotifierType.FetchError,
        buildNotifierErrorProps
    });

    const isDownloadSumaReportVisible = useCallback((asyncRequest: Partial<ParticipantAsyncRequest>) => {
        if (asyncRequest.type !== AsyncRequestTypeName.SumaDocumentVerificationSDK) {
            return false;
        }

        return !!getVerificationId(asyncRequest);
    }, [getVerificationId]);

    const sortedAsyncRequests = useMemo(() => orderBy(asyncRequests,
        [
            (asyncRequest) => {
                const statusName = asyncRequest.status?.name;
                if (statusName === AsyncRequestStatusName.Success) {
                    return 2;
                } else if (statusName === AsyncRequestStatusName.Failed) {
                    return 1;
                }
                return 0;
            },
            (asyncRequest) => indexOf(TYPES_ORDER, asyncRequest.type)
        ],
        ['desc', 'asc']
    ), [asyncRequests]);

    return (
        <InformationModal open={open} title={t(T_KEYS.TITLE)} fullWidth maxWidth='xs' onClose={onClose}>
            <ParticipantName participant={participant} />
            {map(sortedAsyncRequests, (asyncRequest) => {
                const statusName = asyncRequest.status?.name;
                const status = statusName ?
                    t(T_KEYS_SESSION.REQUEST_STATUS[buildTranslationKey(asyncRequest.status.name)]) :
                    t(T_KEYS.NO_STATUS);
                const type = buildTranslationKey(asyncRequest.type);
                const hasCompleteStatus = statusName === AsyncRequestStatusName.Success || statusName === AsyncRequestStatusName.Failed;
                return (
                    <div key={asyncRequest.type} className='tw-flex tw-my-4 tw-items-center'>
                        {hasCompleteStatus ? <StatusIcon
                            success={asyncRequest.status?.name === AsyncRequestStatusName.Success}
                            error={asyncRequest.status?.name === AsyncRequestStatusName.Failed}
                        /> : <div className={cx('tw-rounded-full tw-w-4 tw-h-4 tw-bg-default tw-flex-shrink-0')} />}
                        <div className={cx('tw-ml-4', {
                            'tw-text-default-dimmed': !hasCompleteStatus
                        })}
                        >
                            {t(T_KEYS_SESSION.ASYNC_REQUEST.TYPE[type])}
                            <span>{` (${status})`}</span>
                        </div>
                        {isDownloadSumaReportVisible(asyncRequest) &&
                            <Tooltip message={t(T_KEYS.DOWNLOAD_REPORT)} disabled={sumaReportLoading}>
                                {sumaReportLoading ?
                                    <div className='tw-relative tw-w-4 tw-h-4 tw-ml-4'>
                                        <Loader />
                                    </div> :
                                    <Icon
                                        name='download'
                                        button
                                        group
                                        color='primary'
                                        className='tw-ml-4'
                                        disabled={sumaReportLoading}
                                        onClick={() => getSumaReport(asyncRequest)}
                                    />}
                            </Tooltip>}
                    </div>
                );
            })}
        </InformationModal>
    );
};
